/**
 * Class Thinking Gaps tab — full list of every gap pattern in this
 * (batch, subject), sorted by current student count desc.
 *
 * Per spec §7:
 *   - Includes all statuses: active, clearing, cleared-this-week
 *   - Each row: dot + node_name + chapter + misconception + structural_rule
 *     + count pill ("N of T students") + since pill + status badge + count bar
 *   - misconception is full text (not truncated)
 *   - structural_rule rendered in italic with left border accent
 *
 * Empty state per plan §4.7:
 *   "No thinking gaps detected in {Subject} for this batch."
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function StatusBadge({ state }) {
  const map = {
    active:   { label: 'Open',                 cls: 'bg-teacher-red-light text-teacher-red' },
    clearing: { label: 'In progress',          cls: 'bg-teacher-amber-light text-teacher-amber' },
    cleared:  { label: 'Cleared this week',    cls: 'bg-teacher-green-light text-teacher-green' },
  };
  const m = map[state] || map.active;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function dotColor(state) {
  if (state === 'cleared') return 'bg-teacher-green';
  if (state === 'clearing') return 'bg-teacher-amber';
  return 'bg-teacher-red';
}

function countBarColor(state) {
  if (state === 'cleared') return 'bg-teacher-green';
  if (state === 'clearing') return 'bg-teacher-amber';
  return 'bg-teacher-red';
}

function formatChapterName(key) {
  if (!key) return '';
  return key
    .replace(/^(physics|chemistry|mathematics|maths)_/, '')
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatSinceDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `since ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function GapRow({ gap, totalStudents }) {
  const pct = totalStudents > 0
    ? Math.min(100, Math.round((gap.current_count / totalStudents) * 100))
    : 0;

  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-teacher-border last:border-b-0">
      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${dotColor(gap.gap_state)}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-teacher-text-primary mb-0.5">
          {gap.node_name}
        </div>
        <div className="text-[11px] text-teacher-text-secondary mb-1.5">
          {formatChapterName(gap.chapter_key)}
        </div>
        {gap.core_misconception && (
          <div className="text-xs text-teacher-text-secondary leading-relaxed mb-1.5">
            {gap.core_misconception}
          </div>
        )}
        {Array.isArray(gap.structural_rule) && gap.structural_rule.length > 0 && (
          <ol className="text-[11px] text-teacher-text-secondary italic border-l-2 border-teacher-border-strong pl-2 leading-relaxed list-decimal list-inside space-y-0.5">
            {gap.structural_rule.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        )}
        <div className="flex flex-wrap gap-2 mt-2.5 items-center">
          <span className="text-[11px] bg-teacher-bg-secondary border border-teacher-border text-teacher-text-secondary px-2 py-0.5 rounded-full">
            {gap.current_count} of {totalStudents} students
          </span>
          <span className="text-[11px] bg-teacher-bg-secondary border border-teacher-border text-teacher-text-secondary px-2 py-0.5 rounded-full">
            {formatSinceDate(gap.first_seen_date)}
          </span>
          <StatusBadge state={gap.gap_state} />
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className={`text-[13px] font-medium ${
          gap.gap_state === 'cleared' ? 'text-teacher-green' :
          gap.gap_state === 'clearing' ? 'text-teacher-amber' :
          'text-teacher-red'
        }`}>
          {gap.current_count} students
        </div>
        <div className="w-20 h-1 rounded-full bg-teacher-border overflow-hidden mt-1.5">
          <div className={`h-full rounded-full ${countBarColor(gap.gap_state)}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function ContextClassGapsTab() {
  const { batchId, subject } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getTeacherClassGapsV2(batchId, subject)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [batchId, subject]);

  if (loading) {
    return <div className="p-6 text-sm text-teacher-text-secondary">Loading…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-teacher-red-light border border-teacher-red text-teacher-red rounded-lg p-3 text-sm">
          Error: {error}
        </div>
      </div>
    );
  }

  const subjectLabel = SUBJECT_LABEL[subject] || subject;
  const gaps = data?.gaps || [];
  const totalStudents = data?.context?.total_students || 0;

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="text-base font-semibold text-teacher-text-primary mb-1">
          Thinking gaps across your batch · last 60 days
        </h1>
        <p className="text-xs text-teacher-text-secondary leading-relaxed">
          Each gap shows the exact reasoning error Atlas detected, drawn from
          the micro-lesson content. Spread = students affected. Persistence =
          days pattern has been visible.
        </p>
      </div>

      <div className="bg-white border border-teacher-border rounded-xl px-5 py-2">
        {gaps.length === 0 ? (
          <div className="py-8 text-center text-sm text-teacher-text-secondary">
            No thinking gaps detected in {subjectLabel} for this batch.
          </div>
        ) : (
          gaps.map(gap => (
            <GapRow key={gap.atlas_node_id} gap={gap} totalStudents={totalStudents} />
          ))
        )}
      </div>
    </div>
  );
}
